'use client'

import React, { useState, useCallback } from 'react'
import Image from 'next/image'
import type { VideoEmbedBlock as VideoEmbedBlockProps, Video, Media } from '@/payload-types'
import { cn } from '@/utilities/ui'
import { getMediaUrl } from '@/utilities/getMediaUrl'
import { parseVideoUrl } from '@/utilities/parseVideoUrl'
import { formatDurationISO8601 } from '@/utilities/formatDurationISO8601'
import { safeJsonLdStringify } from '@/components/structuredData'
import { PlayButton } from './PlayButton'
import { getBlockPadding } from '@/utilities/getBlockPadding'
import { VideoWatchLink } from '@/components/VideoWatchLink'

type Props = VideoEmbedBlockProps & {
  disableInnerContainer?: boolean
}

// Type guard for populated Video relationship
function isPopulatedVideo(video: VideoEmbedBlockProps['video']): video is Video {
  return typeof video === 'object' && video !== null && 'videoUrl' in video
}

// Type guard for Media object
function isMediaObject(media: Video['customThumbnail']): media is Media {
  return typeof media === 'object' && media !== null && 'url' in media
}

// Width mapping to Tailwind classes
const widthClasses: Record<string, string> = {
  '100': 'w-full',
  '70': 'w-full lg:w-[70%]',
  '50': 'w-full lg:w-[50%]',
}

// YouTube thumbnail fallback chain (highest to lowest quality)
const YOUTUBE_THUMBNAIL_FALLBACKS = ['maxresdefault', 'sddefault', 'hqdefault', 'mqdefault'] as const

export const VideoEmbedBlock: React.FC<Props> = ({
  video,
  showWatchLink,
  width = '100',
  disableInnerContainer,
  paddingTop,
  paddingBottom,
}) => {
  // Get padding classes based on layout options
  const paddingClasses = getBlockPadding({
    paddingTop: paddingTop ?? true,
    paddingBottom: paddingBottom ?? true,
  })
  const [isPlaying, setIsPlaying] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [thumbnailFallbackIndex, setThumbnailFallbackIndex] = useState(0)

  // Extract video data safely (may be unpopulated string ID or null)
  const populated = video && isPopulatedVideo(video) ? video : null
  const videoUrl = populated?.videoUrl ?? null
  const customThumbnail = populated?.customThumbnail ?? null
  const title = populated?.title ?? null
  const parsedVideo = videoUrl ? parseVideoUrl(videoUrl) : null

  // Determine thumbnail URL with fallback support for YouTube
  const getThumbnailUrl = useCallback(() => {
    if (customThumbnail && isMediaObject(customThumbnail)) {
      return getMediaUrl(customThumbnail.url, customThumbnail.updatedAt)
    }
    // Auto-fetch for YouTube with fallback chain
    if (parsedVideo?.provider === 'youtube' && parsedVideo.videoId) {
      const quality = YOUTUBE_THUMBNAIL_FALLBACKS[thumbnailFallbackIndex] || 'mqdefault'
      return `https://img.youtube.com/vi/${parsedVideo.videoId}/${quality}.jpg`
    }
    return null
  }, [customThumbnail, parsedVideo, thumbnailFallbackIndex])

  // Handle thumbnail load error - try next fallback
  const handleThumbnailError = useCallback(() => {
    if (
      parsedVideo?.provider === 'youtube' &&
      !customThumbnail &&
      thumbnailFallbackIndex < YOUTUBE_THUMBNAIL_FALLBACKS.length - 1
    ) {
      setThumbnailFallbackIndex((prev) => prev + 1)
    }
  }, [parsedVideo, customThumbnail, thumbnailFallbackIndex])

  const handlePlay = useCallback(() => {
    setIsLoading(true)
    setIsPlaying(true)
  }, [])

  const handleIframeLoad = useCallback(() => {
    setIsLoading(false)
  }, [])

  // Early returns after all hooks
  if (!populated || !parsedVideo) {
    return null
  }

  const { description, uploadDate, duration, contentUrl, transcript } = populated
  const thumbnailUrl = getThumbnailUrl()

  // If no thumbnail available (Vimeo without custom), don't render
  if (!thumbnailUrl) {
    console.warn('VideoEmbed: No thumbnail available for video')
    return null
  }

  // Always build JSON-LD schema (title is required on the collection)
  const jsonLdSchema = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: title,
    description: description || undefined,
    thumbnailUrl: thumbnailUrl,
    embedUrl: parsedVideo.embedUrl,
    uploadDate: uploadDate ? new Date(uploadDate).toISOString() : undefined,
    duration: duration ? formatDurationISO8601(duration) : undefined,
    contentUrl: contentUrl || undefined,
    transcript: transcript || undefined,
  }

  return (
    <>
      {/* JSON-LD Schema */}
      <script
        id={`video-schema-${parsedVideo.videoId}`}
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: safeJsonLdStringify(jsonLdSchema) }}
      />

      {/* Video Container */}
      <div
        className={cn(paddingClasses, 'flex justify-center', {
          container: !disableInnerContainer,
        })}
      >
        <div className={widthClasses[width || '100'] || widthClasses['100']}>
          <div
            className={cn(
              'relative overflow-hidden rounded-[8px] lg:rounded-[16px]',
              // 16:9 aspect ratio
              'aspect-video',
            )}
          >
            {!isPlaying ? (
              // Facade: Thumbnail + Play Button
              <>
                {/* Thumbnail Image */}
                <Image
                  src={thumbnailUrl}
                  alt={title || 'Video thumbnail'}
                  fill
                  className="object-cover"
                  sizes="(max-width: 1024px) 100vw, 70vw"
                  unoptimized={thumbnailUrl.startsWith('https://img.youtube.com')}
                  onError={handleThumbnailError}
                />

                {/* Semi-transparent dark overlay */}
                <div className="absolute inset-0 bg-black/30 z-[1]" />

                {/* Play Button */}
                <PlayButton onClick={handlePlay} />
              </>
            ) : (
              // Loading state + Iframe
              <>
                {/* Shimmer loading placeholder */}
                {isLoading && (
                  <div className="absolute inset-0 bg-civo-black z-[1]">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer motion-reduce:animate-none" />
                  </div>
                )}

                {/* Iframe */}
                <iframe
                  src={`${parsedVideo.embedUrl}?autoplay=1`}
                  title={title || 'Embedded video'}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  sandbox="allow-scripts allow-same-origin allow-presentation"
                  className="absolute inset-0 w-full h-full"
                  onLoad={handleIframeLoad}
                />
              </>
            )}
          </div>
          {showWatchLink === true && (
            <VideoWatchLink
              href={parsedVideo.watchUrl}
              provider={parsedVideo.provider}
              title={title}
            />
          )}
        </div>
      </div>
    </>
  )
}
